import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SectionBackground from "./SectionBackground";
import ContactFormModal from "./ContactFormModal";
import CustomSolutionModal from "./CustomSolutionModal";

const pricingPlans = [
  {
    name: "Entry Level",
    features: [
      "2GB RAM",
      "20GB SSD Storage",
      "Weekly Backups",
      "DDoS Protection",
      "Full Root Access",
      "24/7/365 Tech Support",
    ],
    recommended: false,
  },
  {
    name: "Business",
    features: [
      "4GB RAM",
      "50GB SSD Storage",
      "Weekly Backups",
      "DDoS Protection",
      "Full Root Access",
      "24/7/365 Tech Support",
    ],
    recommended: true,
  },
  {
    name: "Enterprise",
    features: [
      "8GB RAM",
      "100GB SSD Storage",
      "Weekly Backups",
      "DDoS Protection",
      "Full Root Access",
      "24/7/365 Tech Support",
    ],
    recommended: false,
  },
];

const Pricing: React.FC = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>("");

  const handleContactUs = (planName: string) => {
    setSelectedPlan(planName);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleOpenCustomModal = () => {
    setCustomModalOpen(true);
  };

  const handleCloseCustomModal = () => {
    setCustomModalOpen(false);
  };

  // Generate templated message based on plan
  const getContactTemplate = () => {
    const plan = pricingPlans.find((p) => p.name === selectedPlan);
    if (!plan) return { subject: "", message: "" };

    const subject = `Pricing Inquiry - ${plan.name} Tier Plan`;
    const message = `Hello,

I am interested in the ${plan.name} Tier hosting plan with the following specifications:

${plan.features.map((f) => `- ${f}`).join("\n")}

Please provide me with:
- Detailed pricing information
- Contract terms and payment options
- Setup timeline and process
- Any additional costs or fees
- Available add-ons or upgrades

Additional information about my requirements:
[Please describe your specific use case, expected traffic, any special requirements, etc.]

Thank you for your time.`;

    return { subject, message };
  };

  const { subject, message } = getContactTemplate();
  return (
    <Box
      id="pricing"
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <SectionBackground variant="default" />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "3rem" },
              background: "linear-gradient(135deg, #ffffff 0%, #00a8ff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Pricing
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", maxWidth: 700, mx: "auto" }}
          >
            Choose the perfect plan for your needs. Contact us for custom
            pricing tailored to your requirements.
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {pricingPlans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "background.paper",
                  border: plan.recommended ? "2px solid" : "1px solid",
                  borderColor: plan.recommended
                    ? "primary.main"
                    : "rgba(0, 168, 255, 0.2)",
                  position: "relative",
                  transition: "all 0.3s ease",
                  transform: plan.recommended ? "scale(1.03)" : "scale(1)",
                  "&:hover": {
                    transform: plan.recommended ? "scale(1.05)" : "scale(1.02)",
                    boxShadow: plan.recommended
                      ? "0 16px 48px rgba(0, 168, 255, 0.4)"
                      : "0 12px 40px rgba(0, 168, 255, 0.3)",
                    borderColor: "primary.main",
                  },
                }}
              >
                {plan.recommended && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      bgcolor: "primary.main",
                      color: "white",
                      px: 2,
                      py: 0.75,
                      borderRadius: 999,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      zIndex: 1,
                    }}
                  >
                    RECOMMENDED
                  </Box>
                )}
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: 4,
                    pt: plan.recommended ? 5 : 4,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 1,
                      fontWeight: 700,
                      textAlign: "center",
                      color: plan.recommended ? "primary.main" : "text.primary",
                    }}
                  >
                    {plan.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mb: 4,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    Contact us for pricing
                  </Typography>

                  <List sx={{ mb: 3, flexGrow: 1 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon
                            sx={{ color: "primary.main", fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: { fontWeight: 500 },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    onClick={() => handleContactUs(plan.name)}
                    variant={plan.recommended ? "contained" : "outlined"}
                    fullWidth
                    size="large"
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      ...(plan.recommended
                        ? {
                            background:
                              "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #5dcdff 0%, #3385db 100%)",
                              boxShadow: "0 8px 24px rgba(0, 168, 255, 0.4)",
                            },
                          }
                        : {
                            borderColor: "primary.main",
                            color: "primary.main",
                            "&:hover": {
                              borderColor: "primary.light",
                              bgcolor: "rgba(0, 168, 255, 0.1)",
                            },
                          }),
                    }}
                  >
                    Contact Us
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
            Need a custom solution? We can tailor our services to your specific
            requirements.
          </Typography>
          <Button
            onClick={handleOpenCustomModal}
            variant="outlined"
            size="large"
            sx={{
              borderColor: "primary.main",
              color: "primary.main",
              px: 4,
              "&:hover": {
                borderColor: "primary.light",
                bgcolor: "rgba(0, 168, 255, 0.1)",
              },
            }}
          >
            Request Custom Quote
          </Button>
        </Box>
      </Container>

      <ContactFormModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        templateSubject={subject}
        templateMessage={message}
        showDatacenterOptions={true}
      />

      <CustomSolutionModal
        open={customModalOpen}
        onClose={handleCloseCustomModal}
      />
    </Box>
  );
};

export default Pricing;
